import { Address, Chain, LocalAccount, Transport } from "viem";
import { isSignerWallet, SignerWallet } from "../../../types/shared/evm";
import { ExactEvmPayload, PaymentPayload, PaymentRequirements } from "../../../types/verify";
import { createNonce, signAuthorization } from "./sign";
import { encodePayment } from "./utils/paymentUtils";

// Type for unsigned EIP-3009 authorization payload
type UnsignedAuthorizationPayload = Omit<PaymentPayload, "payload"> & {
  payload: Omit<ExactEvmPayload, "signature"> & { signature: undefined };
};

/**
 * Prepares an unsigned payment header with the given sender address and payment requirements.
 *
 * @param from - The sender's address from which the payment will be made
 * @param x402Version - The version of the X402 protocol to use
 * @param paymentRequirements - The payment requirements containing scheme and network information
 * @returns An unsigned payment payload containing authorization details
 */
export function preparePaymentHeader(
  from: Address,
  x402Version: number,
  paymentRequirements: PaymentRequirements,
): UnsignedAuthorizationPayload {
  const nonce = createNonce();

  const validAfter = BigInt(
    Math.floor(Date.now() / 1000) - 600, // 10 minutes before
  ).toString();
  const validBefore = BigInt(
    Math.floor(Date.now() / 1000 + paymentRequirements.maxTimeoutSeconds),
  ).toString();

  // For cross-chain/swap payments, send to the appropriate wallet:
  // - recipient (preferred): Pre-allocated wallet from quote for swaps
  // - globalWalletAddress (deprecated): Backward compatible alias for recipient
  // - facilitatorAddress (fallback): Static facilitator EOA for legacy flow
  // For direct payments (same chain, same token), send directly to payTo
  const isCrossChainOrSwap =
    paymentRequirements.srcNetwork !== paymentRequirements.network ||
    (paymentRequirements.srcTokenAddress &&
      paymentRequirements.srcTokenAddress.toLowerCase() !== paymentRequirements.asset?.toLowerCase());

  // Prefer recipient for cross-chain swaps (allows authorization.to to be set correctly)
  // Fall back to globalWalletAddress (deprecated) or facilitatorAddress for backward compatibility
  const toAddress =
    isCrossChainOrSwap && paymentRequirements.extra?.recipient
      ? (paymentRequirements.extra.recipient as Address)
      : isCrossChainOrSwap && paymentRequirements.extra?.globalWalletAddress
        ? (paymentRequirements.extra.globalWalletAddress as Address)
        : isCrossChainOrSwap && paymentRequirements.extra?.facilitatorAddress
          ? (paymentRequirements.extra.facilitatorAddress as Address)
          : (paymentRequirements.payTo as Address);

  return {
    x402Version,
    scheme: paymentRequirements.scheme,
    network: paymentRequirements.srcNetwork ?? paymentRequirements.network,
    payload: {
      signature: undefined,
      authorization: {
        from,
        to: toAddress,
        value: paymentRequirements.srcAmountRequired || paymentRequirements.maxAmountRequired,
        validAfter: validAfter.toString(),
        validBefore: validBefore.toString(),
        nonce,
      },
    },
  };
}

/**
 * Signs a payment header using the provided client and payment requirements.
 *
 * @param client - The signer wallet instance used to sign the payment header
 * @param paymentRequirements - The payment requirements containing scheme and network information
 * @param unsignedPaymentHeader - The unsigned payment payload to be signed
 * @returns A promise that resolves to the signed payment payload
 */
export async function signPaymentHeader<transport extends Transport, chain extends Chain>(
  client: SignerWallet<chain, transport> | LocalAccount,
  paymentRequirements: PaymentRequirements,
  unsignedPaymentHeader: UnsignedAuthorizationPayload,
): Promise<PaymentPayload> {
  const { signature } = await signAuthorization(
    client,
    unsignedPaymentHeader.payload.authorization,
    paymentRequirements,
  );

  return {
    ...unsignedPaymentHeader,
    payload: {
      ...unsignedPaymentHeader.payload,
      signature,
    },
  };
}

/**
 * Creates a complete payment payload by preparing and signing a payment header.
 *
 * @param client - The signer wallet instance used to create and sign the payment
 * @param x402Version - The version of the X402 protocol to use
 * @param paymentRequirements - The payment requirements containing scheme and network information
 * @returns A promise that resolves to the complete signed payment payload
 */
export async function createPayment<transport extends Transport, chain extends Chain>(
  client: SignerWallet<chain, transport> | LocalAccount,
  x402Version: number,
  paymentRequirements: PaymentRequirements,
): Promise<PaymentPayload> {
  const from = isSignerWallet(client) ? client.account!.address : client.address;
  const unsignedPaymentHeader = preparePaymentHeader(from, x402Version, paymentRequirements);
  return signPaymentHeader(client, paymentRequirements, unsignedPaymentHeader);
}

/**
 * Creates and encodes a payment header for the given client and payment requirements.
 *
 * @param client - The signer wallet instance used to create the payment header
 * @param x402Version - The version of the X402 protocol to use
 * @param paymentRequirements - The payment requirements containing scheme and network information
 * @returns A promise that resolves to the encoded payment header string
 */
export async function createPaymentHeader(
  client: SignerWallet | LocalAccount,
  x402Version: number,
  paymentRequirements: PaymentRequirements,
): Promise<string> {
  const payment = await createPayment(client, x402Version, paymentRequirements);
  return encodePayment(payment);
}
